import { useMemo } from "react";

const AGENTS = [
  { role: "market_analyst",       label: "Market",         sub: "technicals"  },
  { role: "fundamentals_analyst", label: "Fundamentals",   sub: "valuation"   },
  { role: "bull_researcher",      label: "Bull",           sub: "long thesis" },
  { role: "bear_researcher",      label: "Bear",           sub: "short thesis"},
  { role: "risk_manager",         label: "Risk",           sub: "risk gate"   },
  { role: "portfolio_manager",    label: "PM",             sub: "head of desk"},
];

const STATE_LABEL = {
  running:  "run",
  speaking: "live",
  done:     "done",
  idle:     "idle",
};

/**
 * Given a run doc + a "recentSpeakingRole" (set by the parent as agents
 * emit transcript entries), compute a per-agent state:
 *   speaking → lavender highlight (last ~2s after entry)
 *   running  → gold dot + gold-tinted row border
 *   done     → dim green tick
 *   idle     → grey dot
 */
export default function AgentRoster({ run, speakingRole }) {
  const states = useMemo(() => {
    const spoken = new Set();
    for (const e of run?.transcript || []) {
      if (e.role && e.role !== "system") spoken.add(e.role);
    }
    const running = run?.status === "running";
    return AGENTS.map((a) => {
      let state = "idle";
      if (spoken.has(a.role)) state = a.role === speakingRole && running ? "speaking" : "done";
      else if (running) state = "running";
      if (!running && !spoken.has(a.role)) state = "idle";
      return { ...a, state };
    });
  }, [run, speakingRole]);

  const activeCount = states.filter((s) => s.state !== "idle").length;

  return (
    <div data-testid="agent-roster" className="flex h-full flex-col">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="aurion-section-title">Roster</div>
        <div className="font-mono text-[9px] tabular-nums text-[var(--aurion-muted)]">
          {activeCount}/6
        </div>
      </div>
      <div className="aurion-scroll flex-1 space-y-1 overflow-auto pr-1">
        {states.map((a) => (
          <div
            key={a.role}
            data-testid={`agent-${a.role}`}
            data-state={a.state}
            className={`agent-row agent-row-${a.state} flex items-center gap-2 border-l-2 px-2 py-1.5`}
          >
            <div className={`agent-lamp ${a.state}`} />
            <div className="flex-1 min-w-0">
              <div className="truncate text-[11px] font-semibold leading-tight text-[var(--aurion-text)]">
                {a.label}
              </div>
              <div className="truncate text-[9px] uppercase tracking-widest leading-tight text-[var(--aurion-muted)]">
                {a.sub}
              </div>
            </div>
            <div className={`font-mono text-[9px] uppercase tracking-widest ${
              a.state === "speaking"
                ? "text-[var(--aurion-lavender)]"
                : a.state === "running"
                ? "text-[var(--aurion-gold)]"
                : a.state === "done"
                ? "text-[var(--aurion-green)]"
                : "text-[var(--aurion-muted-strong)]"
            }`}>
              {STATE_LABEL[a.state]}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
